import React from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import {
  Box, Building, ChevronDown, ChevronRight, Folder, Globe, Layers, Network,
  Pencil, Plus, Server, Shield, Star, Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import { countDevicesInSubtree, devicesInGroup } from '../groupTree';

const ICON_MAP = {
  folder: Folder,
  server: Server,
  network: Network,
  globe: Globe,
  star: Star,
  box: Box,
  layers: Layers,
  building: Building,
  shield: Shield,
};

const COLOR_CLASSES = {
  default: 'bg-gray-100 text-gray-500',
  blue: 'bg-blue-100 text-blue-600',
  green: 'bg-green-100 text-green-600',
  yellow: 'bg-yellow-100 text-yellow-600',
  red: 'bg-red-100 text-red-600',
  purple: 'bg-purple-100 text-purple-600',
  orange: 'bg-orange-100 text-orange-600',
  teal: 'bg-teal-100 text-teal-600',
  gray: 'bg-gray-100 text-gray-600',
};

/**
 * Linha de grupo dentro da tabela de devices.
 *
 * Dois gestos distintos, como o usuário pediu:
 *  - "+" (caret) → expande o grupo NA MESMA lista, sem sair do nível atual;
 *  - clique na linha → navega PARA DENTRO do grupo (breadcrumb assume).
 *
 * A linha também é alvo de drop (arrastar device/grupo para dentro) e ela
 * própria arrastável (mover o grupo para outro grupo).
 */
const GroupRow = ({
  node,
  depth = 0,
  columnCount,
  typeDevices,
  expandedGroups,
  onToggle,
  onOpen,
  onEdit,
  onDelete,
  renderDeviceRow,
  t,
}) => {
  const isExpanded = expandedGroups.has(node.id);
  const children = node.children || [];
  const directDevices = devicesInGroup(typeDevices, node.id);
  const subtreeCount = countDevicesInSubtree(node, typeDevices);
  const IconComponent = ICON_MAP[node.iconKey] || Folder;

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `group-${node.id}`,
    data: { type: 'group', groupId: node.id },
  });

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `folder-${node.id}`,
    data: { type: 'folder', groupId: node.id },
  });

  const setRefs = (el) => {
    setDropRef(el);
    setDragRef(el);
  };

  return (
    <>
      <tr
        ref={setRefs}
        {...listeners}
        {...attributes}
        onClick={() => onOpen(node)}
        className={clsx(
          'cursor-pointer select-none bg-gray-50/70 hover:bg-gray-100',
          isOver && 'ring-2 ring-inset ring-theme-primary bg-theme-primary/5',
          isDragging && 'opacity-40'
        )}
      >
        <td className="px-6 py-3" style={depth > 0 ? { paddingLeft: `${24 + depth * 24}px` } : undefined}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
              title={isExpanded ? t('managedDevices.groups.collapse') : t('managedDevices.groups.expand')}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
            >
              {isExpanded ? <ChevronDown size={14} /> : <Plus size={14} />}
            </button>
            <div className={clsx('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', COLOR_CLASSES[node.color] || COLOR_CLASSES.default)}>
              {node.customIconBase64 ? (
                <img src={node.customIconBase64} alt="" className="h-7 w-7 rounded object-cover" />
              ) : (
                <IconComponent size={18} />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1 font-medium text-gray-900">
                {node.name}
                <ChevronRight size={14} className="text-gray-400" />
              </div>
              {node.description && <div className="text-sm text-gray-500 line-clamp-1">{node.description}</div>}
            </div>
          </div>
        </td>
        <td className="px-6 py-3" colSpan={Math.max(1, columnCount - 2)}>
          <span className="text-sm text-gray-500">
            {t('managedDevices.groups.deviceCount', { count: subtreeCount })}
            {children.length > 0 && ` · ${t('managedDevices.groups.subGroupCount', { count: children.length })}`}
          </span>
        </td>
        <td className="px-6 py-3 text-right" onClick={(e) => e.stopPropagation()}>
          <div className="flex w-full justify-end gap-1">
            <button
              onClick={() => onEdit(node)}
              title={t('groups.editGroup')}
              className="p-1 text-gray-600 hover:bg-gray-100 rounded"
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={() => onDelete(node)}
              title={t('groups.deleteGroup')}
              className="p-1 text-red-500 hover:bg-red-50 rounded"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </td>
      </tr>

      {isExpanded && children.map((child) => (
        <GroupRow
          key={child.id}
          node={child}
          depth={depth + 1}
          columnCount={columnCount}
          typeDevices={typeDevices}
          expandedGroups={expandedGroups}
          onToggle={onToggle}
          onOpen={onOpen}
          onEdit={onEdit}
          onDelete={onDelete}
          renderDeviceRow={renderDeviceRow}
          t={t}
        />
      ))}

      {isExpanded && directDevices.map((device) => renderDeviceRow(device, depth + 1))}

      {isExpanded && children.length === 0 && directDevices.length === 0 && (
        <tr>
          <td
            colSpan={columnCount}
            className="px-6 py-3 text-sm text-gray-400"
            style={{ paddingLeft: `${24 + (depth + 1) * 24}px` }}
          >
            {t('managedDevices.groups.emptyGroup')}
          </td>
        </tr>
      )}
    </>
  );
};

export default GroupRow;
